import type { ActivityChartData, CountryEvent, CountryPeriod, TimeRange } from 'tacho-activity-chart';
import {
  countryPeriodsFromEvents,
  normalizeDriverDiagram,
  toChartData,
} from './diagram-normalizer';

/**
 * The driver `getDiagramDataForPeriod` payload as it arrives from the backend.
 *
 * The Angular service types describe the same fields but use slightly
 * different names, so the mapping below is deliberately structural and tolerant
 * of both shapes.
 */
export interface DriverDiagramActivityLike {
  type?: number;
  day?: number;
  startTime?: number;
  endTime?: number;
  duration?: number;
  cardSlot?: number;
  typeString?: string;
  entryType?: string;
  drivingStatus?: number;
}

export interface DriverCountryPlaceLike {
  entryTime?: number;
  startCountry?: string | null;
  endCountry?: string | null;
  country?: string | null;
  startCountryCode?: string | number | null;
  endCountryCode?: string | number | null;
}

export interface DriverDiagramDayLike {
  day: number;
  driverName?: string | null;
  cardNumber?: string | null;
  activities?: DriverDiagramActivityLike[] | null;
  vehicles?: unknown[] | null;
  startCountry?: DriverCountryPlaceLike | null;
  endCountry?: DriverCountryPlaceLike | null;
  outOfScopes?: { start: number; end: number }[] | null;
  ferryInfos?: number[] | null;
  /** New place list (IN/OUT records) from the diagram endpoint. */
  places?: Array<{
    type?: number;
    entryTime?: number;
    startCountry?: string | null;
    endCountry?: string | null;
    startCountryCode?: number | null;
    endCountryCode?: number | null;
  }> | null;
}

/**
 * Builds the `tacho-activity-chart` model for a driver period.
 *
 * The payload is normalised to UTC activity instants first (see
 * `normalizeDriverDiagram`) and only then laid out on the user's local days.
 */
export function buildDriverChartData(
  days: DriverDiagramDayLike[] | null | undefined,
  timezone?: string,
  period?: TimeRange,
): ActivityChartData {
  return toChartData(normalizeDriverDiagram(days), timezone, period);
}

/**
 * Every country entry recorded by the driver endpoint, as UTC instants.
 *
 * The same country can be entered several times in a row, so these events are
 * kept even when they do not change the country.
 */
export function buildCountryEvents(days: DriverDiagramDayLike[] | null | undefined): CountryEvent[] {
  return normalizeDriverDiagram(days).countryEvents;
}

/**
 * Turns the country entry events into absolute UTC stays.
 *
 * Consecutive events in the same country are merged into one stay, even when
 * they are on different day buckets.
 */
export function buildCountryPeriods(
  days: DriverDiagramDayLike[] | null | undefined,
  countryEvents?: CountryEvent[],
): CountryPeriod[] {
  const diagram = normalizeDriverDiagram(days);
  if (!countryEvents) return diagram.countries;

  const periodEnd = Math.max(
    0,
    ...diagram.days.map((day) => day.day + 24 * 60 * 60),
    ...diagram.activities.map((activity) => activity.end),
  );
  return countryPeriodsFromEvents(countryEvents, periodEnd);
}

export { buildActivityChartStrings as buildDriverChartStrings } from './chart-strings';
